import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { QuestionType } from '../../../generated/hooks';
import { AnswerGroupTable, ColumnType, type TableColumn } from '../components/AnswerGroupTable';
import type { NewReport } from '../hooks/useNewReport';
import { answeredRows } from '../definition/tableRows';
import { Action, Bar } from '../definition/editor/styles';
import { ScaleCard } from '../components/ScaleCard';
import { findScaleMetricForQuestion, questionSummaryKey } from '@arnold/core';
import { useDefinitionMetrics } from '../definition/ReportDefinitionContext';

export function QuestionDetailPage({ report }: { report: NewReport }) {
  const { questionIndex } = useParams<{ questionIndex: string }>();
  const { t } = useTranslation('newReport');
  const question = report.rawData?.questions.find((item) => item.index === questionIndex);
  const open = question?.type === QuestionType.Freetext;
  const isScaleQuestion = question?.type === QuestionType.Scale;
  const root = report.currentGroup;
  const metrics = useDefinitionMetrics();
  const scaleMetric =
    question?.type === QuestionType.Scale ? findScaleMetricForQuestion(metrics, questionIndex) : undefined;
  const metricSummary = scaleMetric ? root?.customMetrics[scaleMetric.id] : undefined;
  const [withTeams, setWithTeams] = useState(false);
  const mode = !open || withTeams ? 'teamsAndParticipants' : 'participants';
  const hasThemes = !!report.rawData?.questionLabels.find((label) => label.questionDefinitionIndex === questionIndex)
    ?.questionLabelComments?.length;
  const columns = useMemo((): TableColumn[] => {
    const identity: TableColumn = {
      id: 'identity',
      type: ColumnType.LABEL,
      label: t(open && !withTeams ? 'definition.participant' : 'definition.teamParticipant'),
      width: 180,
    };
    if (!open)
      return [
        identity,
        {
          id: 'responses',
          type: ColumnType.QUESTION_ANSWER_COUNT,
          label: t('definition.responses'),
          questionIndex,
          width: 110,
        },
        { id: 'choices', type: ColumnType.CHOICES, label: t('table.columns.choices'), questionIndex },
      ];
    return [
      identity,
      { id: 'team', type: ColumnType.TEAM, label: t('table.columns.team'), width: 140 },
      { id: 'text', type: ColumnType.TEXT, label: t('definition.answer'), questionIndex, source: 'text' },
      ...(hasThemes
        ? [
            {
              id: 'themes',
              type: ColumnType.THEMES as const,
              label: t('table.columns.themes'),
              questionIndex,
              source: 'text' as const,
            },
          ]
        : []),
    ];
  }, [questionIndex, open, hasThemes, withTeams, t]);
  const { selectRows, filteredData } = report;
  const rows = useMemo(
    () => answeredRows(selectRows(filteredData ?? [], mode), questionIndex),
    [selectRows, filteredData, mode, questionIndex],
  );

  if (!question) return <p>{t('definition.unavailable')}</p>;
  return (
    <div style={{ maxWidth: 1000 }}>
      {!isScaleQuestion && open && (
        <Bar>
          <Action active={!withTeams} onClick={() => setWithTeams(false)}>
            {t('definition.rows.participants')}
          </Action>
          <Action active={withTeams} onClick={() => setWithTeams(true)}>
            {t('definition.rows.teamsAndParticipants')}
          </Action>
        </Bar>
      )}
      {!isScaleQuestion && (
        <AnswerGroupTable
          label={question.text}
          groups={rows}
          columns={columns}
          sortingEnabled
          searchEnabled
          resetKey={JSON.stringify([questionIndex, mode, report.filters.answerSelections, [...report.filters.teamIds]])}
        />
      )}
      {isScaleQuestion &&
        (question.statementsLocal?.length ? question.statementsLocal : [undefined]).map((statement, index) => {
          const statementIndex = statement === undefined ? undefined : index;
          const summary = root?.questionSummaries.get(questionSummaryKey(question.index, statementIndex));
          return (
            <ScaleCard
              key={`${question.index}-${statementIndex ?? 'question'}`}
              question={question}
              statement={statement}
              summary={summary}
              metricSummary={metricSummary}
              eligibleCount={root?.responseIds.size ?? 0}
            />
          );
        })}
    </div>
  );
}
